import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type {
  KeyboardEvent as ReactKeyboardEvent,
  PointerEvent as ReactPointerEvent,
} from "react";
import {
  GAP_INFO,
  LIMIT_GAP,
  LIMIT_MAX,
  LIMIT_MIN_GAP,
  LIVE_Y,
  ORDER_KEYS,
  SCENARIOS,
  SPREAD_PIPS,
  STOP_DEFAULT,
  STOP_MAX,
  STOP_MIN,
  areaPolygon,
  firstCrossDown,
  getPath,
  pipsBetween,
  priceAtY,
  resolveStopLimit,
  strokePolygon,
  yAtX,
} from "@/components/feature/pricePath/paths";
import type {
  Cross,
  MarketMode,
  OrderKey,
  Placement,
  Tone,
} from "@/components/feature/pricePath/paths";
import { useReplay } from "@/components/feature/pricePath/useReplay";
import Eyebrow from "@/components/base/Eyebrow";

interface Marker {
  id: string;
  x: number;
  y: number;
  tone: Tone;
  tag: string;
  placement: Placement;
  miss?: boolean;
}

interface Beat {
  at: number;
  text: string;
}

type Unit = "pips" | "money";
type DragTarget = "stop" | "limit";

const toneColor: Record<Tone, string> = {
  primary: "oklch(var(--primary-500))",
  accent: "oklch(var(--accent-500))",
  muted: "oklch(var(--background-50))",
};

const placementClass: Record<Placement, string> = {
  top: "bottom-full left-1/2 -translate-x-1/2 mb-2",
  bottom: "top-full left-1/2 -translate-x-1/2 mt-2",
  left: "right-full top-1/2 -translate-y-1/2 mr-2",
  right: "left-full top-1/2 -translate-y-1/2 ml-2",
};

const fmt = (value: number): string => value.toFixed(5);

/* EUR/USD: one standard lot is worth roughly $10 per pip. */
const PIP_VALUE_USD = 10;
const LOT_OPTIONS = [0.1, 0.25, 0.5, 1];
const TOUR_STEP_MS = 3600;

interface PricePathChartProps {
  defaultKey?: OrderKey;
  eyebrow?: string;
  title?: string;
  description?: string;
}

export default function PricePathChart({
  defaultKey = "market",
  eyebrow = "Visual guide",
  title = "Where each order fires on the chart",
  description = "One price path, four orders. Press play to let it teach itself, drag the handles to move your stop and limit, and flip between a calm and a gap market to see exactly where each order triggers — or fails to fill.",
}: PricePathChartProps) {
  const [activeKey, setActiveKey] = useState<OrderKey>(defaultKey);
  const [mode, setMode] = useState<MarketMode>("calm");
  const [yStop, setYStop] = useState<number>(STOP_DEFAULT);
  const [yLimit, setYLimit] = useState<number>(STOP_DEFAULT + LIMIT_GAP);
  const [dragTarget, setDragTarget] = useState<DragTarget | null>(null);
  const [unit, setUnit] = useState<Unit>("pips");
  const [lots, setLots] = useState<number>(1);
  const [tolerance, setTolerance] = useState<number>(10);
  const [narration, setNarration] = useState(true);
  const [touring, setTouring] = useState(false);
  const [runId, setRunId] = useState(0);

  const plotRef = useRef<HTMLDivElement>(null);
  const mountedRef = useRef(false);
  const [plotHeight, setPlotHeight] = useState(0);

  const { progress, skip } = useReplay(runId);

  const replay = useCallback(() => setRunId((value) => value + 1), []);

  /* Replay whenever the scenario or the market mode changes. */
  useEffect(() => {
    if (!mountedRef.current) {
      mountedRef.current = true;
      return;
    }
    replay();
  }, [activeKey, mode, replay]);

  /* Track the plot height so axis labels can be nudged apart on short viewports. */
  useEffect(() => {
    const el = plotRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const update = () => setPlotHeight(el.getBoundingClientRect().height);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  /* "Four-in-a-row" auto tour — plays the four scenarios once, then stops on the last. */
  useEffect(() => {
    if (!touring) return undefined;
    const index = ORDER_KEYS.indexOf(activeKey);
    if (index >= ORDER_KEYS.length - 1) {
      setTouring(false);
      return undefined;
    }
    const id = window.setTimeout(() => {
      setActiveKey(ORDER_KEYS[index + 1]);
    }, TOUR_STEP_MS);
    return () => window.clearTimeout(id);
  }, [touring, activeKey]);

  const selectKey = (key: OrderKey) => {
    setTouring(false);
    setActiveKey(key);
  };

  /* Launch from the top so a round always plays all four, then halts at the end. */
  const toggleTour = () => {
    if (touring) {
      setTouring(false);
      return;
    }
    setActiveKey(ORDER_KEYS[0]);
    setTouring(true);
  };

  /* Snap the sell stop and its limit back to their default spacing. */
  const resetHandles = () => {
    setYStop(STOP_DEFAULT);
    setYLimit(STOP_DEFAULT + LIMIT_GAP);
  };

  /* Slippage tolerance — how far a stop may slip before it is judged to have failed. */
  const decTolerance = () => setTolerance((value) => Math.max(0, value - 5));
  const incTolerance = () => setTolerance((value) => Math.min(50, value + 5));
  const toggleNarration = () => setNarration((value) => !value);

  const path = getPath(mode);
  const scenario = SCENARIOS.find((item) => item.key === activeKey) ?? SCENARIOS[0];
  const canDrag = activeKey === "stop" || activeKey === "stop-limit";
  const isStopLimit = activeKey === "stop-limit";

  /* ---- Geometry for the (draggable) sell stop ---------------------- */
  const cross: Cross | null = useMemo(() => firstCrossDown(path, yStop), [path, yStop]);

  const fillX = cross ? Math.min(100, cross.x + 1.2) : 0;
  const fillY = cross ? yAtX(path, fillX) : 0;

  /* ---- Geometry for the stop-limit (draggable limit) --------------- */
  const sl = useMemo(() => resolveStopLimit(path, yStop, yLimit), [path, yStop, yLimit]);

  /* ---- Markers for the active scenario ----------------------------- */
  const markers: Marker[] = useMemo(() => {
    const list: Marker[] = [];
    if (activeKey === "market") {
      list.push({ id: "market", x: 4, y: LIVE_Y, tone: "primary", tag: "FILL", placement: "right" });
    }
    if (activeKey === "limit") {
      list.push({ id: "limit-sell", x: 40, y: 20, tone: "primary", tag: "FILL", placement: "top" });
      list.push({ id: "limit-buy", x: 70, y: 66, tone: "primary", tag: "FILL", placement: "left" });
    }
    if (activeKey === "stop") {
      list.push({ id: "stop-buy", x: 28, y: 30, tone: "accent", tag: "TRIGGER", placement: "top" });
      if (cross) {
        list.push({ id: "stop-trigger", x: cross.x, y: yStop, tone: "accent", tag: "TRIGGER", placement: "top" });
        list.push({ id: "stop-fill", x: fillX, y: fillY, tone: "accent", tag: "FILL", placement: "left" });
      }
    }
    if (activeKey === "stop-limit") {
      if (sl.trigger) {
        list.push({ id: "sl-trigger", x: sl.trigger.x, y: yStop, tone: "accent", tag: "TRIGGER", placement: "top" });
      }
      if (sl.fill) {
        if (sl.filled) {
          list.push({ id: "sl-fill", x: sl.fill.x, y: sl.fill.y, tone: "primary", tag: "FILL", placement: "left" });
        } else {
          list.push({ id: "sl-miss", x: sl.fill.x, y: sl.fill.y, tone: "muted", tag: "NO FILL", placement: "left", miss: true });
        }
      }
    }
    return list;
  }, [activeKey, cross, yStop, fillX, fillY, sl]);

  /* ---- Levels (sell stop + its limit follow the drags) ------------- */
  const levels = useMemo(
    () => [
      { key: "sell-limit", y: 20, label: "SELL LIMIT", tone: "primary" as Tone },
      { key: "buy-stop", y: 30, label: "BUY STOP", tone: "accent" as Tone },
      { key: "live", y: LIVE_Y, label: "LIVE PRICE", tone: "muted" as Tone },
      { key: "buy-limit", y: 66, label: "BUY LIMIT", tone: "primary" as Tone },
      { key: "sell-stop", y: yStop, label: "SELL STOP", tone: "accent" as Tone },
      { key: "sl-cap", y: yLimit, label: "S/L LIMIT", tone: "primary" as Tone },
    ],
    [yStop, yLimit],
  );

  /* Push axis labels apart so tightly spaced levels (a stop and its limit)
     never stack on top of each other on short viewports. */
  const axisLevels = useMemo(() => {
    const boxPx = 30;
    const minSep = plotHeight > 0 ? Math.min(14, (boxPx / plotHeight) * 100) : 0;
    const sorted = [...levels].sort((a, b) => a.y - b.y);
    let prevDisp = Number.NEGATIVE_INFINITY;
    const placed = new Map<string, number>();
    sorted.forEach((level) => {
      const disp = Math.max(level.y, prevDisp + minSep);
      placed.set(level.key, disp);
      prevDisp = disp;
    });
    return placed;
  }, [levels, plotHeight]);

  const isLevelActive = (key: string) => scenario.levels.includes(key);

  /* ---- Drag handling ----------------------------------------------- */
  const applyPointer = (clientY: number, target: DragTarget) => {
    if (!plotRef.current) return;
    const rect = plotRef.current.getBoundingClientRect();
    const pct = ((clientY - rect.top) / rect.height) * 100;
    const y = Math.round(pct * 10) / 10;

    if (target === "stop") {
      const nextStop = Math.max(STOP_MIN, Math.min(STOP_MAX, y));
      setYStop(nextStop);
      setYLimit((prev) => Math.max(prev, nextStop + LIMIT_MIN_GAP));
    } else {
      const minLimit = Math.max(yStop + LIMIT_MIN_GAP, STOP_MIN);
      setYLimit(Math.max(minLimit, Math.min(LIMIT_MAX, y)));
    }
  };

  const startDrag = (target: DragTarget, event: ReactPointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDragTarget(target);
    skip();
  };

  const moveDrag = (target: DragTarget, event: ReactPointerEvent<HTMLDivElement>) => {
    if (dragTarget !== target) return;
    applyPointer(event.clientY, target);
  };

  const endDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    setDragTarget(null);
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
  };

  const handleDragKey = (target: DragTarget, event: ReactKeyboardEvent<HTMLDivElement>) => {
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;
    event.preventDefault();
    const step = (event.shiftKey ? 5 : 1) * (event.key === "ArrowDown" ? 1 : -1);

    if (target === "stop") {
      const nextStop = Math.max(STOP_MIN, Math.min(STOP_MAX, yStop + step));
      setYStop(nextStop);
      setYLimit((prev) => Math.max(prev, nextStop + LIMIT_MIN_GAP));
    } else {
      const minLimit = Math.max(yStop + LIMIT_MIN_GAP, STOP_MIN);
      setYLimit((value) => Math.max(minLimit, Math.min(LIMIT_MAX, value + step)));
    }
  };

  /* ---- Unit conversion --------------------------------------------- */
  const toUnit = useCallback(
    (pips: number): string => {
      if (unit === "pips") {
        if (pips === 0) return "0 pips";
        return `${pips < 10 ? pips.toFixed(1) : pips.toFixed(0)} pips`;
      }
      if (pips <= 0) return "$0";
      return `-$${(pips * PIP_VALUE_USD * lots).toFixed(0)}`;
    },
    [unit, lots],
  );

  /* ---- Readout + copy ---------------------------------------------- */
  const slippage = cross ? pipsBetween(yStop, fillY) : 0;

  const readout = useMemo(() => {
    switch (activeKey) {
      case "market":
        return [
          { label: "Entry", value: fmt(priceAtY(LIVE_Y)), alert: false },
          { label: "Spread cost", value: toUnit(SPREAD_PIPS), alert: false },
          { label: "Price control", value: "None", alert: false },
          { label: "Main risk", value: "Slippage", alert: false },
        ];
      case "limit":
        return [
          { label: "Buy limit", value: fmt(priceAtY(66)), alert: false },
          { label: "Sell limit", value: fmt(priceAtY(20)), alert: false },
          { label: "Slippage", value: toUnit(0), alert: false },
          { label: "Main risk", value: "No fill", alert: false },
        ];
      case "stop":
        return [
          { label: "Trigger", value: fmt(priceAtY(yStop)), alert: false },
          { label: "Actual fill", value: cross ? fmt(priceAtY(fillY)) : "—", alert: false },
          { label: "Slippage", value: toUnit(slippage), alert: slippage > tolerance },
          { label: "Main risk", value: "Slippage", alert: false },
        ];
      default:
        return [
          { label: "Trigger", value: fmt(priceAtY(yStop)), alert: false },
          { label: "Limit", value: fmt(priceAtY(yLimit)), alert: false },
          { label: "Result", value: sl.filled ? "Filled" : "Unfilled", alert: !sl.filled },
          {
            label: "Cost",
            value: sl.filled ? toUnit(pipsBetween(yStop, yLimit)) : "No exit",
            alert: !sl.filled,
          },
        ];
    }
  }, [activeKey, yStop, yLimit, fillY, cross, slippage, sl, toUnit, tolerance]);

  const headline = useMemo(() => {
    switch (activeKey) {
      case "market":
        return "Fills the instant you click";
      case "limit":
        return "Waits for the market to reach your price";
      case "stop":
        return mode === "gap" ? "Triggers on the level, fills far past it" : "Triggers and fills right at the level";
      default:
        return sl.filled ? "Triggers and capped by your limit" : "Triggered, but the gap skipped your limit";
    }
  }, [activeKey, mode, sl.filled]);

  const detail = useMemo(() => {
    switch (activeKey) {
      case "market":
        return "A market buy lifts the ask at the live price — 1.08467 here. You are filled immediately, but you accept the spread and whatever the next available price turns out to be.";
      case "limit":
        return "You rest a buy limit below the market and a sell limit above it. Nothing fills until price travels to your level — and it can never fill you worse than your price.";
      case "stop":
        return mode === "gap"
          ? "Once the sell stop triggers it becomes a market order — and the gap rips straight through the trigger, filling you well below the level. That distance between trigger and fill is slippage. Drag the stop to see the gap between the two points grow or shrink."
          : "Once the sell stop triggers it becomes a market order that fills right at the level. In a calm market the trigger price and the fill price sit almost on top of each other. Switch to a gap market to watch them come apart.";
      default:
        return !sl.filled
          ? "The stop triggers and rests a limit just below it — but the gap jumps straight past the limit, so it is never touched and the order stays open. Drag the S/L LIMIT handle closer to the stop: the narrower the gap between them, the easier the limit is to fill. Widen it and a gap market will skip it entirely."
          : "The stop triggers and rests a limit just below it, and the market still trades through that limit, so the order fills. Drag the S/L LIMIT handle to change the spacing to your stop — that distance decides how much slippage protection you keep, and how easily a fast market can leave you unfilled.";
    }
  }, [activeKey, mode, sl.filled]);

  /* Short, in-the-moment captions — the full principle lives in the body text below the chart. */
  const narrationBeats: Beat[] = useMemo(() => {
    switch (activeKey) {
      case "market":
        return [
          { at: 0, text: "Market order placed — no price, no waiting." },
          { at: 35, text: "Filled instantly at the ask." },
          { at: 72, text: "Spread paid. Price never asked permission." },
        ];
      case "limit":
        return [
          { at: 0, text: "Two limits rest — one above, one below." },
          { at: 35, text: "Price drifts up to your sell limit." },
          { at: 72, text: "Filled — at your price, not the market's." },
        ];
      case "stop":
        return mode === "gap"
          ? [
              { at: 0, text: "Sell stop armed, below the market." },
              { at: 40, text: "The gap rips straight through the trigger." },
              { at: 75, text: "Fills far below — that gap is slippage." },
            ]
          : [
              { at: 0, text: "Sell stop armed, below the market." },
              { at: 40, text: "Triggered — it becomes a market order." },
              { at: 75, text: "Filled right at the level." },
            ];
      default:
        return sl.filled
          ? [
              { at: 0, text: "Two prices: a stop that says when, a limit that says how far." },
              { at: 40, text: "Triggered — it rests a limit just below." },
              { at: 75, text: "Price trades through the limit. Filled." },
            ]
          : [
              { at: 0, text: "Two prices: a stop that says when, a limit that says how far." },
              { at: 40, text: "Triggered — it rests a limit just below." },
              { at: 75, text: "The gap skips the limit. No fill." },
            ];
    }
  }, [activeKey, mode, sl.filled]);

  let beatIndex = 0;
  for (let i = 0; i < narrationBeats.length; i += 1) {
    if (progress >= narrationBeats[i].at) beatIndex = i;
  }
  const activeBeat = narrationBeats[beatIndex];

  /* ---- Slippage tolerance — has this stop been rendered ineffective? ---- */
  const stopFailed =
    activeKey === "stop"
      ? slippage > tolerance
      : activeKey === "stop-limit"
        ? !sl.filled
        : false;

  const failMessage =
    activeKey === "stop"
      ? `Stop failed — price slipped ${slippage < 10 ? slippage.toFixed(1) : Math.round(slippage)} pips, beyond your ${tolerance}-pip tolerance.`
      : "Stop failed — the gap skipped your limit, so the position is left open with no guaranteed exit.";

  const showGap = mode === "gap" && progress >= GAP_INFO.x - 2;
  const tipY = yAtX(path, progress);

  return (
    <section className="px-4 md:px-8 py-16 md:py-24 bg-foreground-950">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6">
          <div className="max-w-3xl">
            <Eyebrow tone="onDark">{eyebrow}</Eyebrow>
            <h2 className="mt-4 font-heading font-black uppercase leading-[0.95] tracking-tight text-background-50 text-3xl md:text-5xl">
              {title}
            </h2>
          </div>
          <p className="max-w-md text-sm leading-relaxed text-background-300 lg:pb-2">{description}</p>
        </div>

        <div className="mt-10 border-2 border-primary-500 bg-foreground-950">
          {/* Terminal bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-b-2 border-primary-500">
            <span className="flex items-center gap-2 font-label text-[11px] uppercase tracking-[0.18em] text-primary-500">
              Price path · EUR/USD · intraday
            </span>
            <span className="font-label text-[10px] uppercase tracking-[0.14em] text-background-500">
              Illustrative chart · not live data
            </span>
          </div>

          {/* Tabs + controls */}
          <div className="flex flex-col gap-2.5 px-4 py-3 border-b-2 border-background-50/15 md:flex-row md:flex-wrap md:items-center md:justify-between md:gap-3">
            <div className="-mx-4 flex gap-2 overflow-x-auto px-4 pb-1 md:mx-0 md:flex-wrap md:overflow-visible md:px-0 md:pb-0">
              {SCENARIOS.map((item) => {
                const active = item.key === activeKey;
                return (
                  <button
                    key={item.key}
                    type="button"
                    onClick={() => selectKey(item.key)}
                    className={`inline-flex shrink-0 items-center px-3 py-2 md:px-3.5 border-2 font-label text-[11px] uppercase tracking-[0.14em] font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                      active
                        ? "bg-primary-500 border-primary-500 text-foreground-950"
                        : "border-background-50/25 text-background-300 hover:border-primary-500 hover:text-primary-500"
                    }`}
                  >
                    {item.label}
                  </button>
                );
              })}
            </div>

            <div className="flex items-center gap-2 overflow-x-auto pb-1 md:flex-wrap md:overflow-visible md:pb-0">
              <div className="flex items-center border-2 border-background-50/25">
                {(["calm", "gap"] as MarketMode[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setMode(value)}
                    className={`px-3 py-2 font-label text-[10px] uppercase tracking-[0.14em] font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                      mode === value
                        ? "bg-accent-500 text-foreground-950"
                        : "text-background-300 hover:text-accent-400"
                    }`}
                  >
                    <span className="hidden sm:inline">{value === "calm" ? "Calm market" : "Gap market"}</span>
                    <span className="sm:hidden">{value === "calm" ? "Calm" : "Gap"}</span>
                  </button>
                ))}
              </div>
              <button
                type="button"
                onClick={toggleTour}
                className={`inline-flex shrink-0 items-center gap-2 px-3 py-2 md:px-3.5 border-2 font-label text-[10px] uppercase tracking-[0.14em] font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  touring
                    ? "bg-accent-500 border-accent-500 text-foreground-950"
                    : "border-accent-500 text-accent-400 hover:bg-accent-500 hover:text-foreground-950"
                }`}
              >
                <i className={touring ? "ri-pause-fill" : "ri-play-fill"}></i>
                <span className="hidden md:inline">{touring ? "Touring" : "Auto tour"}</span>
              </button>
              {canDrag && (
                <button
                  type="button"
                  onClick={resetHandles}
                  className="inline-flex shrink-0 items-center gap-2 px-3 py-2 md:px-3.5 border-2 border-background-50/25 text-background-300 font-label text-[10px] uppercase tracking-[0.14em] font-semibold whitespace-nowrap hover:border-accent-500 hover:text-accent-400 transition-colors cursor-pointer"
                >
                  <i className="ri-restart-line"></i>
                  <span className="hidden md:inline">Reset handles</span>
                </button>
              )}
              <button
                type="button"
                onClick={replay}
                className="inline-flex shrink-0 items-center gap-2 px-3 py-2 md:px-3.5 border-2 border-primary-500 text-primary-500 font-label text-[10px] uppercase tracking-[0.14em] font-semibold whitespace-nowrap hover:bg-primary-500 hover:text-foreground-950 transition-colors cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                <span className="hidden md:inline">Replay</span>
              </button>
              <button
                type="button"
                onClick={toggleNarration}
                aria-pressed={narration}
                className={`inline-flex shrink-0 items-center gap-2 px-3 py-2 md:px-3.5 border-2 font-label text-[10px] uppercase tracking-[0.14em] font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                  narration
                    ? "border-accent-500 text-accent-400"
                    : "border-background-50/25 text-background-500 hover:text-background-300"
                }`}
              >
                <i className="ri-chat-quote-line"></i>
                <span className="hidden md:inline">{narration ? "Captions on" : "Captions off"}</span>
              </button>
            </div>
          </div>

          {/* Tour progress strip */}
          {touring && (
            <div className="flex items-center gap-2 px-4 py-2 border-b-2 border-background-50/15 bg-background-50/5">
              <span className="font-label text-[9px] uppercase tracking-[0.18em] text-accent-400">
                Now playing
              </span>
              <div className="flex flex-1 gap-1">
                {ORDER_KEYS.map((key) => (
                  <span
                    key={key}
                    className={`h-1.5 flex-1 overflow-hidden ${
                      key === activeKey ? "bg-background-50/20" : "bg-background-50/10"
                    }`}
                  >
                    {key === activeKey && (
                      <span
                        key={runId}
                        className="tourbar block h-full bg-accent-500"
                        style={{ animation: `tourbar ${TOUR_STEP_MS}ms linear forwards` }}
                      ></span>
                    )}
                  </span>
                ))}
              </div>
              <span className="font-label text-[9px] uppercase tracking-[0.18em] text-background-400">
                {String(ORDER_KEYS.indexOf(activeKey) + 1).padStart(2, "0")} / 04
              </span>
            </div>
          )}

          {/* Chart */}
          <div className="p-4 md:p-6">
            <div
              role="group"
              aria-label={`Illustrative price chart showing where a ${scenario.label} order triggers and fills in a ${mode} market`}
              className="relative w-full aspect-[6/5] md:aspect-[16/9] lg:aspect-[21/9] bg-foreground-950 overflow-hidden"
            >
              <div ref={plotRef} className="absolute inset-0 right-[4.5rem] md:right-24">
                <div className="absolute inset-0 grid-pattern-light opacity-40 pointer-events-none"></div>

                {/* Price path (revealed left → right) */}
                <div
                  className="absolute inset-0"
                  style={{ clipPath: `inset(0 ${100 - progress}% 0 0)` }}
                >
                  <div
                    className="absolute inset-0"
                    style={{
                      clipPath: `polygon(${areaPolygon(path)})`,
                      backgroundColor: "oklch(var(--primary-500) / 0.12)",
                    }}
                  ></div>
                  <div
                    className="absolute inset-0"
                    style={{
                      clipPath: `polygon(${strokePolygon(path)})`,
                      backgroundColor: "oklch(var(--primary-500))",
                    }}
                  ></div>
                </div>

                {/* Drawing head */}
                {progress < 100 && (
                  <div className="absolute z-20 pointer-events-none" style={{ left: `${progress}%`, top: `${tipY}%` }}>
                    <span className="block w-3 h-3 -translate-x-1/2 -translate-y-1/2 bg-primary-500 border-2 border-foreground-950"></span>
                  </div>
                )}

                {/* Gap highlight */}
                <div
                  className="absolute border-x-2 border-accent-500 bg-accent-500/20 transition-opacity duration-300 pointer-events-none"
                  style={{
                    left: `${GAP_INFO.x - 1}%`,
                    top: `${GAP_INFO.yTop}%`,
                    width: "2%",
                    height: `${GAP_INFO.yBottom - GAP_INFO.yTop}%`,
                    opacity: showGap ? 1 : 0,
                  }}
                ></div>

                {/* Order levels */}
                {levels.map((level) => {
                  const active = isLevelActive(level.key);
                  const colorKey =
                    level.tone === "primary"
                      ? "var(--primary-500)"
                      : level.tone === "accent"
                        ? "var(--accent-500)"
                        : "var(--background-50)";
                  return (
                    <div
                      key={level.key}
                      className="absolute left-0 right-0 border-t-2 border-dashed transition-colors duration-300"
                      style={{
                        top: `${level.y}%`,
                        borderColor: active
                          ? `oklch(${colorKey} / 0.9)`
                          : "oklch(var(--background-50) / 0.13)",
                      }}
                    ></div>
                  );
                })}

                {/* Draggable sell-stop handle (left) */}
                {canDrag && (
                  <div
                    role="slider"
                    aria-label="Sell stop price level"
                    aria-valuemin={STOP_MIN}
                    aria-valuemax={STOP_MAX}
                    aria-valuenow={yStop}
                    aria-valuetext={`${fmt(priceAtY(yStop))}`}
                    tabIndex={0}
                    onPointerDown={(event) => startDrag("stop", event)}
                    onPointerMove={(event) => moveDrag("stop", event)}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onKeyDown={(event) => handleDragKey("stop", event)}
                    className={`absolute left-0 z-30 -translate-y-1/2 flex items-center gap-1.5 pl-1.5 pr-2.5 py-1 border-2 border-accent-500 bg-foreground-950 cursor-ns-resize touch-none select-none focus:outline-none focus:bg-accent-500 group ${
                      dragTarget === "stop" ? "ring-2 ring-accent-400" : ""
                    }`}
                    style={{ top: `${yStop}%` }}
                  >
                    <i className="ri-drag-move-2-line text-accent-500 group-focus:text-foreground-950"></i>
                    <span className="font-label text-[9px] uppercase tracking-[0.12em] text-accent-500 group-focus:text-foreground-950">
                      Sell stop · {fmt(priceAtY(yStop))}
                    </span>
                    <span className="ml-0.5 hidden items-center gap-0.5 group-hover:inline-flex group-focus:inline-flex">
                      <i className="ri-arrow-up-s-line text-accent-500 group-focus:text-foreground-950"></i>
                      <i className="ri-arrow-down-s-line text-accent-500 group-focus:text-foreground-950"></i>
                    </span>
                  </div>
                )}

                {/* Draggable limit handle (right) — stop-limit only */}
                {isStopLimit && (
                  <div
                    role="slider"
                    aria-label="Stop-limit limit price level"
                    aria-valuemin={Math.round((yStop + LIMIT_MIN_GAP) * 10) / 10}
                    aria-valuemax={LIMIT_MAX}
                    aria-valuenow={yLimit}
                    aria-valuetext={`${fmt(priceAtY(yLimit))} · gap ${pipsBetween(yStop, yLimit).toFixed(0)} pips`}
                    tabIndex={0}
                    onPointerDown={(event) => startDrag("limit", event)}
                    onPointerMove={(event) => moveDrag("limit", event)}
                    onPointerUp={endDrag}
                    onPointerCancel={endDrag}
                    onKeyDown={(event) => handleDragKey("limit", event)}
                    className={`absolute right-0 z-30 -translate-y-1/2 flex items-center gap-1.5 pr-1.5 pl-2.5 py-1 border-2 border-primary-500 bg-foreground-950 cursor-ns-resize touch-none select-none focus:outline-none focus:bg-primary-500 group ${
                      dragTarget === "limit" ? "ring-2 ring-primary-400" : ""
                    }`}
                    style={{ top: `${yLimit}%` }}
                  >
                    <span className="font-label text-[9px] uppercase tracking-[0.12em] text-primary-500 group-focus:text-foreground-950">
                      Limit · {fmt(priceAtY(yLimit))}
                    </span>
                    <i className="ri-drag-move-2-line text-primary-500 group-focus:text-foreground-950"></i>
                    <span className="ml-0.5 hidden items-center gap-0.5 group-hover:inline-flex group-focus:inline-flex">
                      <i className="ri-arrow-up-s-line text-primary-500 group-focus:text-foreground-950"></i>
                      <i className="ri-arrow-down-s-line text-primary-500 group-focus:text-foreground-950"></i>
                    </span>
                  </div>
                )}

                {/* Markers */}
                {markers.map((marker) => {
                  const revealed = progress >= marker.x - 0.5;
                  const color = toneColor[marker.tone];
                  return (
                    <div
                      key={marker.id}
                      className="absolute z-10"
                      style={{ left: `${marker.x}%`, top: `${marker.y}%` }}
                    >
                      <div className="relative -translate-x-1/2 -translate-y-1/2">
                        <span
                          className="block rounded-full border-2 transition-all duration-300"
                          style={{
                            width: revealed ? 15 : 9,
                            height: revealed ? 15 : 9,
                            backgroundColor: revealed ? color : "oklch(var(--background-50) / 0.35)",
                            borderColor: revealed ? "oklch(var(--background-50))" : "transparent",
                          }}
                        ></span>

                        {revealed && (
                          <>
                            <span
                              className="absolute inset-0 rounded-full border-2 animate-ping"
                              style={{ borderColor: color, opacity: 0.5 }}
                            ></span>
                            <span
                              className={`absolute z-20 whitespace-nowrap border-2 px-2 py-1 font-label text-[9px] uppercase tracking-[0.12em] font-semibold ${placementClass[marker.placement]}`}
                              style={{
                                backgroundColor: "oklch(var(--foreground-950))",
                                borderColor: color,
                                color,
                              }}
                            >
                              {marker.miss && <i className="ri-close-line mr-1"></i>}
                              {marker.tag}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Price axis */}
              <div className="absolute inset-y-0 right-0 w-[4.5rem] md:w-24 border-l-2 border-background-50/20 bg-foreground-950">
                {levels.map((level) => {
                  const active = isLevelActive(level.key);
                  const color = toneColor[level.tone];
                  const draggable =
                    (level.key === "sell-stop" && canDrag) || (level.key === "sl-cap" && isStopLimit);
                  return (
                    <div
                      key={level.key}
                      className="absolute right-1 -translate-y-1/2 transition-all duration-300 md:right-1.5"
                      style={{ top: `${axisLevels.get(level.key) ?? level.y}%` }}
                    >
                      <div
                        className={`border-2 px-1 py-0.5 text-right transition-colors duration-300 md:px-1.5 ${
                          draggable ? (level.tone === "accent" ? "border-accent-500" : "border-primary-500") : ""
                        }`}
                        style={
                          active || draggable
                            ? {
                                borderColor: color,
                                backgroundColor: color,
                                color: "oklch(var(--foreground-950))",
                              }
                            : {
                                borderColor: "oklch(var(--background-50) / 0.18)",
                                color: "oklch(var(--background-50) / 0.55)",
                              }
                        }
                      >
                        <span className="block font-label text-[8px] uppercase tracking-[0.12em] whitespace-nowrap">
                          {level.label}
                        </span>
                        <span className="block font-label text-[9px] font-semibold md:text-[10px]">
                          {fmt(priceAtY(level.y))}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Time axis */}
            <div className="mt-2 flex justify-between pr-[4.5rem] md:pr-24 font-label text-[9px] uppercase tracking-[0.14em] text-background-500">
              <span>09:00</span>
              <span>11:00</span>
              <span>13:00</span>
              <span>15:00</span>
              <span>17:00</span>
            </div>

            {/* Hint */}
            {isStopLimit ? (
              <p className="mt-3 flex flex-wrap items-center gap-2 font-label text-[10px] uppercase tracking-[0.12em] text-accent-400 md:mt-4">
                <i className="ri-drag-move-2-line"></i>
                Drag the <span className="text-accent-300">Sell stop</span> handle, and the{" "}
                <span className="text-primary-400">Limit</span> handle, to change the gap between them. Narrow gap = protected but easy to fill. Wide gap + a fast market = skipped, no fill.
              </p>
            ) : canDrag ? (
              <p className="mt-3 flex flex-wrap items-center gap-2 font-label text-[10px] uppercase tracking-[0.12em] text-accent-400 md:mt-4">
                <i className="ri-drag-move-2-line"></i>
                Drag the sell stop handle — or use ↑ ↓ — to move your stop and watch the trigger and fill follow.
              </p>
            ) : (
              <p className="mt-3 flex flex-wrap items-center gap-2 font-label text-[10px] uppercase tracking-[0.12em] text-background-500 md:mt-4">
                The gap sits late on the path, so it only changes orders that trigger after it — open a stop order to see the difference.
              </p>
            )}

            {/* Legend */}
            <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 font-label text-[10px] uppercase tracking-[0.12em] text-background-400 md:mt-4 md:flex md:flex-wrap md:items-center md:gap-x-6">
              <span className="flex items-center gap-2">
                <span className="inline-block w-6 h-0.5 bg-primary-500"></span>
                Price path
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-6 border-t-2 border-dashed border-background-50/50"></span>
                Order level
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 rounded-full bg-accent-500"></span>
                Trigger / fill
              </span>
              <span className="flex items-center gap-2">
                <span className="inline-block w-2.5 h-2.5 border-2 border-accent-500 bg-accent-500/20"></span>
                Gap
              </span>
            </div>
          </div>

          {/* Narration caption — plays out with the animation */}
          {narration && (
            <div className="flex items-start gap-3 px-4 md:px-6 py-4 border-t-2 border-background-50/15 bg-foreground-900">
              <p
                key={`${activeKey}-${mode}-${beatIndex}-${sl.filled}-${runId}`}
                className="caption-in font-body text-sm md:text-base leading-relaxed text-background-100"
              >
                {activeBeat.text}
              </p>
            </div>
          )}

          {/* Readout toolbar — unit + lot size */}
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t-2 border-primary-500 bg-foreground-900">
            <span className="flex items-center gap-2 font-label text-[10px] uppercase tracking-[0.18em] text-background-500">
              Readout
            </span>
            <div className="flex flex-wrap items-center gap-3 md:gap-5">
              {canDrag && (
                <div className="flex items-center gap-2">
                  <span className="font-label text-[10px] uppercase tracking-[0.14em] text-background-500">
                    Max slippage
                  </span>
                  <div className="flex items-center border-2 border-background-50/25">
                    <button
                      type="button"
                      onClick={decTolerance}
                      aria-label="Decrease slippage tolerance"
                      disabled={tolerance <= 0}
                      className="px-2 py-1.5 text-background-300 transition-colors hover:text-accent-400 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    >
                      <i className="ri-subtract-line text-xs"></i>
                    </button>
                    <span className="min-w-[3.4rem] text-center font-label text-[10px] font-semibold tracking-[0.08em] text-background-50">
                      {tolerance} pips
                    </span>
                    <button
                      type="button"
                      onClick={incTolerance}
                      aria-label="Increase slippage tolerance"
                      disabled={tolerance >= 50}
                      className="px-2 py-1.5 text-background-300 transition-colors hover:text-accent-400 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    >
                      <i className="ri-add-line text-xs"></i>
                    </button>
                  </div>
                </div>
              )}
              {unit === "money" && (
                <div className="flex items-center gap-2">
                  <span className="font-label text-[10px] uppercase tracking-[0.14em] text-background-500">
                    Lots
                  </span>
                  <div className="relative flex items-center">
                    <select
                      value={lots}
                      onChange={(event) => setLots(Number(event.target.value))}
                      aria-label="Position size in standard lots"
                      className="appearance-none cursor-pointer border-2 border-background-50/25 bg-foreground-950 pl-2.5 pr-7 py-1.5 font-label text-[10px] font-semibold tracking-[0.08em] text-background-50 whitespace-nowrap transition-colors hover:border-primary-500 focus:outline-none focus:border-primary-500"
                    >
                      {LOT_OPTIONS.map((value) => (
                        <option key={value} value={value} className="bg-foreground-950">
                          {value.toFixed(2)}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line pointer-events-none absolute right-1.5 text-sm text-background-400"></i>
                  </div>
                </div>
              )}
              <div className="flex items-center border-2 border-background-50/25">
                {(["pips", "money"] as Unit[]).map((value) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setUnit(value)}
                    className={`px-3 py-1.5 font-label text-[10px] uppercase tracking-[0.14em] font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                      unit === value
                        ? "bg-primary-500 text-foreground-950"
                        : "text-background-300 hover:text-primary-500"
                    }`}
                  >
                    {value === "pips" ? "Pips" : "Money"}
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Stop-failure warning — slippage blew past your tolerance */}
          {stopFailed && (
            <div className="flex items-start gap-3 px-4 md:px-6 py-3 border-t-2 border-accent-500 bg-accent-500/15">
              <i className="ri-error-warning-fill mt-0.5 text-lg text-accent-400"></i>
              <p className="font-label text-[11px] md:text-xs uppercase tracking-[0.08em] leading-relaxed text-accent-300">
                {failMessage}
              </p>
            </div>
          )}

          {/* Readout */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-0.5 bg-background-50/15 border-t-2 border-background-50/15">
            {readout.map((cell) => (
              <div
                key={cell.label}
                className={`px-4 py-4 ${cell.alert ? "bg-accent-500/15" : "bg-foreground-950"}`}
              >
                <p
                  className={`font-label text-[10px] uppercase tracking-[0.16em] ${
                    cell.alert ? "text-accent-400" : "text-background-500"
                  }`}
                >
                  {cell.label}
                </p>
                <p
                  className={`mt-1.5 font-heading font-black uppercase text-xl md:text-2xl leading-none ${
                    cell.alert ? "text-accent-400" : "text-background-50"
                  }`}
                >
                  {cell.value}
                </p>
              </div>
            ))}
          </div>

          {/* Description */}
          <div className="px-4 md:px-6 py-5 border-t-2 border-background-50/15 bg-foreground-900">
            <p className="font-heading font-bold uppercase tracking-tight text-lg text-background-50">
              {headline}
            </p>
            <p className="mt-2 max-w-3xl text-sm leading-relaxed text-background-300">{detail}</p>
          </div>
        </div>
      </div>
    </section>
  );
}